import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { dto } from '@photo-vault/shared';
import { Button, Card, Icon, Input, PageHeader, Tag } from '../../ui/index.js';
import { useAuthSession } from '../../auth/AuthContext.js';
import { useInfra } from '../../infra/context.js';
import { ApiError } from '../../lib/api.js';

type Stage =
  | { kind: 'loading' }
  | { kind: 'connected'; status: dto.BucketStatusResponse }
  | { kind: 'ready'; tpl: dto.OnboardingTemplateResponse }
  | { kind: 'load-error'; message: string };

export function ConnectBucketPage() {
  const session = useAuthSession();
  const { metadata } = useInfra();
  const [stage, setStage] = useState<Stage>({ kind: 'loading' });
  const [accountId, setAccountId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Storage admin is AWS-only; the methods are optional on MetadataStore so
  // a P2P provider can omit them. This page is unreachable from a P2P bundle.
  const fetchBucketStatus = metadata.fetchBucketStatus;
  const fetchOnboardingTemplate = metadata.fetchOnboardingTemplate;
  const connectBucket = metadata.connectBucket;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!fetchBucketStatus || !fetchOnboardingTemplate) {
        setStage({ kind: 'load-error', message: 'bucket onboarding non disponibile' });
        return;
      }
      try {
        const status = await fetchBucketStatus(session.jwt);
        if (cancelled) return;
        if (status.connected) {
          setStage({ kind: 'connected', status });
          return;
        }
        const tpl = await fetchOnboardingTemplate(session.jwt);
        if (cancelled) return;
        setStage({ kind: 'ready', tpl });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError ? err.message : 'Impossibile contattare il backend.';
        setStage({ kind: 'load-error', message });
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [session.jwt, fetchBucketStatus, fetchOnboardingTemplate]);

  async function handleConnect(event: React.FormEvent) {
    event.preventDefault();
    setSubmitError(null);
    if (!connectBucket) {
      setSubmitError('connessione bucket non disponibile');
      return;
    }
    if (!/^\d{12}$/.test(accountId)) {
      setSubmitError('Inserisci esattamente 12 cifre.');
      return;
    }
    setSubmitting(true);
    try {
      const status = await connectBucket({ accountId }, session.jwt);
      setStage({ kind: 'connected', status });
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Connessione fallita. Riprova fra qualche secondo.';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  }

  if (stage.kind === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-on-surface-variant">
        <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-cyan border-r-transparent" />
        <p className="text-sm">Verifico lo stato del bucket…</p>
      </div>
    );
  }

  if (stage.kind === 'load-error') {
    return (
      <div className="flex flex-col gap-5 py-2">
        <Tag tone="error">errore di rete</Tag>
        <Card>
          <p className="text-sm text-on-surface-variant">{stage.message}</p>
        </Card>
        <BackHomeLink />
      </div>
    );
  }

  if (stage.kind === 'connected') {
    return (
      <ConnectedView
        status={stage.status}
        token={session.jwt}
        wipeBucket={metadata.wipeBucket}
        onReset={() => location.reload()}
      />
    );
  }

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="onboarding bucket"
        title={
          <>
            Collega il tuo <span className="accent-text">bucket S3</span>
          </>
        }
        description="Le tue foto restano nel tuo account AWS: noi non le ospitiamo, non le vediamo, non conserviamo le credenziali. Lanciamo uno stack CloudFormation che crea bucket e ruolo cross-account, poi colleghiamo il tutto con un solo dato: il tuo Account ID."
      />

      <StepCard
        index={1}
        title="Lancia lo stack su AWS"
        body={
          <>
            <p className="text-sm text-on-surface-variant">
              Apre la console AWS con il template già pre-compilato. Accetta e clicca{' '}
              <span className="font-semibold text-on-surface">Create stack</span>. Tempo: ~1 minuto.
            </p>
            <a
              href={stage.tpl.launchUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="encryption-glow inline-flex h-12 items-center justify-center gap-2 self-start rounded-lg bg-cyan px-6 font-display font-semibold text-on-primary transition-all hover:brightness-110"
            >
              <Icon name="rocket_launch" className="text-lg" />
              Apri AWS Console
            </a>
            <details className="text-xs text-on-surface-variant">
              <summary className="cursor-pointer">Cosa crea esattamente?</summary>
              <ul className="mt-2 list-disc space-y-1 pl-5">
                <li>Un bucket S3 (cifrato a riposo, accesso pubblico bloccato).</li>
                <li>
                  Un IAM Role di nome <code>{stage.tpl.stackName}-backend-role</code> con trust solo
                  verso il nostro account <code>{stage.tpl.appAwsAccountId}</code> + condizione{' '}
                  <code>ExternalId</code>.
                </li>
                <li>
                  Permessi sul ruolo: lettura/scrittura sul bucket appena creato, niente altro.
                </li>
              </ul>
            </details>
          </>
        }
      />

      <StepCard
        index={2}
        title='Aspetta lo stato "CREATE_COMPLETE"'
        body={
          <p className="text-sm text-on-surface-variant">
            Lo stack ci mette un minuto buono a creare bucket e ruolo. Quando vedi{' '}
            <span className="font-semibold text-cyan">CREATE_COMPLETE</span> nella console AWS,
            torna qui e prosegui.
          </p>
        }
      />

      <StepCard
        index={3}
        title="Incolla il tuo Account ID AWS"
        body={
          <form className="flex flex-col gap-4" onSubmit={handleConnect}>
            <p className="text-sm text-on-surface-variant">
              Sono le 12 cifre in alto a destra nella console AWS, oppure nel campo{' '}
              <span className="font-semibold text-on-surface">Account</span> della tua identità IAM.
            </p>
            <Input
              label="AWS Account ID"
              placeholder="123456789012"
              inputMode="numeric"
              maxLength={12}
              value={accountId}
              onChange={(e) => setAccountId(e.target.value.replace(/\D/g, '').slice(0, 12))}
              {...(submitError ? { error: submitError } : {})}
              leftIcon={<Icon name="badge" />}
              autoComplete="off"
            />
            <Button type="submit" loading={submitting} leftIcon={<Icon name="link" />}>
              Verifica e collega
            </Button>
            <p className="text-xs text-on-surface-variant">
              L&apos;app proverà ad assumere il ruolo creato nello stack, troverà il bucket via tag
              e farà un test di scrittura/lettura/cancellazione su un oggetto innocuo.
            </p>
          </form>
        }
      />

      <BackHomeLink />
    </div>
  );
}

function ConnectedView({
  status,
  token,
  wipeBucket,
  onReset,
}: {
  status: dto.BucketStatusResponse;
  token: string;
  wipeBucket: ((token: string) => Promise<dto.BucketWipeResponse>) | undefined;
  onReset: () => void;
}) {
  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        eyebrow="bucket connesso"
        title={
          <>
            Tutto <span className="accent-text">pronto</span>
          </>
        }
        description="Il tuo bucket è collegato. Le foto vengono cifrate sul dispositivo e caricate direttamente lì: il backend fa solo da postino con presigned URL temporanei."
      />

      <Card className="flex flex-col gap-3">
        <Field label="Bucket" value={status.bucketName ?? '—'} icon="folder_managed" />
        <Field label="Region" value={status.region ?? '—'} icon="public" />
        <Field label="AWS Account" value={status.accountId ?? '—'} icon="badge" />
        <Field label="Role ARN" value={status.roleArn ?? '—'} icon="vpn_key" small />
      </Card>

      <div className="flex flex-wrap gap-3">
        <Button leftIcon={<Icon name="refresh" />} variant="ghost" onClick={onReset}>
          Ricomincia onboarding
        </Button>
        <BackHomeLink />
      </div>

      <WipeSection token={token} wipeBucket={wipeBucket} onWiped={onReset} />
    </div>
  );
}

function WipeSection({
  token,
  wipeBucket,
  onWiped,
}: {
  token: string;
  wipeBucket: ((token: string) => Promise<dto.BucketWipeResponse>) | undefined;
  onWiped: () => void;
}) {
  const [armed, setArmed] = useState(false);
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<dto.BucketWipeResponse | null>(null);

  async function handleWipe(event: React.FormEvent) {
    event.preventDefault();
    if (!wipeBucket) {
      setError('wipe non disponibile per questa istanza');
      return;
    }
    if (confirm !== 'WIPE') {
      setError('Devi digitare esattamente WIPE per confermare.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const out = await wipeBucket(token);
      setResult(out);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Wipe fallito. Riprova fra poco.');
    } finally {
      setLoading(false);
    }
  }

  if (result) {
    return (
      <Card className="flex flex-col gap-3 border-cyan/40">
        <div className="flex items-center gap-2">
          <Icon name="check_circle" className="text-cyan" />
          <h3 className="font-display text-lg font-semibold">Wipe completato</h3>
        </div>
        <ul className="text-sm text-on-surface-variant">
          <li>Oggetti S3 cancellati: {result.deletedObjects}</li>
          <li>Multipart upload abortiti: {result.abortedUploads}</li>
          <li>Album cancellati: {result.deletedAlbums}</li>
          <li>Foto cancellate: {result.deletedPhotos}</li>
        </ul>
        <p className="text-xs text-on-surface-variant">
          Il bucket S3 e lo stack CloudFormation restano nel tuo account AWS. Puoi rilanciare
          l&apos;onboarding per riconnetterli (o cancellarli a mano dalla console se vuoi
          ricominciare con un bucket nuovo).
        </p>
        <Button variant="ghost" leftIcon={<Icon name="refresh" />} onClick={onWiped}>
          Ricarica pagina
        </Button>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col gap-3 border-error/40">
      <div className="flex items-center gap-2">
        <Icon name="warning" className="text-error" />
        <h3 className="font-display text-lg font-semibold">Zona pericolosa</h3>
      </div>
      <p className="text-sm text-on-surface-variant">
        Svuota completamente il bucket: cancella tutte le foto, gli album e l&apos;associazione del
        bucket. Operazione <span className="font-semibold text-on-surface">irreversibile</span>. Il
        bucket S3 e lo stack CloudFormation restano nel tuo account AWS (li gestisci da lì).
      </p>
      {!armed ? (
        <Button
          variant="ghost"
          leftIcon={<Icon name="delete_forever" />}
          onClick={() => setArmed(true)}
        >
          Voglio cancellare tutto
        </Button>
      ) : (
        <form className="flex flex-col gap-3" onSubmit={handleWipe}>
          <Input
            label="Digita WIPE per confermare"
            placeholder="WIPE"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            {...(error ? { error } : {})}
            autoComplete="off"
          />
          <div className="flex gap-3">
            <Button
              type="submit"
              variant="danger"
              loading={loading}
              leftIcon={<Icon name="delete_forever" />}
              disabled={confirm !== 'WIPE'}
            >
              Cancella tutto
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setArmed(false);
                setConfirm('');
                setError(null);
              }}
            >
              Annulla
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

function StepCard({ index, title, body }: { index: number; title: string; body: React.ReactNode }) {
  return (
    <Card className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-full border border-cyan/40 bg-cyan/10 font-display text-cyan">
          {index}
        </span>
        <h3 className="font-display text-lg font-semibold">{title}</h3>
      </div>
      <div className="flex flex-col gap-3">{body}</div>
    </Card>
  );
}

function Field({
  label,
  value,
  icon,
  small = false,
}: {
  label: string;
  value: string;
  icon: string;
  small?: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon name={icon} className="mt-0.5 text-cyan" />
      <div className="flex min-w-0 flex-col">
        <span className="text-xs font-semibold uppercase tracking-[0.08em] text-on-surface-variant">
          {label}
        </span>
        <span
          className={
            small
              ? 'break-all font-display text-xs text-on-surface'
              : 'font-display text-base text-on-surface'
          }
        >
          {value}
        </span>
      </div>
    </div>
  );
}

function BackHomeLink() {
  return (
    <Link
      to="/"
      className="inline-flex items-center gap-2 self-start text-xs uppercase tracking-[0.08em] text-on-surface-variant transition-colors hover:text-cyan"
    >
      <Icon name="arrow_back" className="text-base" />
      Torna alla home
    </Link>
  );
}
